import Link from 'next/link';
import { FooterSections } from '@/types/types';

const renderIcon = (iconType: string | null | undefined) => {
  switch (iconType) {
    case 'email':
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className='size-5 shrink-0 text-gray-300'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          strokeWidth='2'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
          />
        </svg>
      );
    case 'phone':
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className='size-5 shrink-0 text-gray-300'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          strokeWidth='2'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
          />
        </svg>
      );
    case 'location':
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          className='size-5 shrink-0 text-gray-300'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
          strokeWidth='2'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
          />
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
          />
        </svg>
      );
    default:
      return null;
  }
};

interface FooterLinksProps {
  sectionsData?: FooterSections;
}

export default function FooterLinks({ sectionsData }: FooterLinksProps) {
  // Fallback data in case Builder.io data is not available
  const defaultSections = [
    {
      title: 'ABOUT US',
      sectionType: 'links' as const,
      links: [{ text: 'Company History', url: '/about' as const }],
    },
    {
      title: 'SUPPORT',
      sectionType: 'links' as const,
      links: [
        { text: 'Contact Us', url: '/contact' as const },
        { text: 'FAQ', url: '/help-center' as const },
      ],
    },
    {
      title: 'SERVICES',
      sectionType: 'links' as const,
      links: [
        { text: 'Designer Series', url: '/' as const },
        {
          text: 'Custom Solutions',
          url: 'https://www.envisioncreates.com/' as const,
        },
      ],
    },
    {
      title: 'CONTACT US',
      sectionType: 'contact' as const,
      links: [
        {
          text: 'Email Us',
          url: 'mailto:info@envisioncreates.com' as const,
          iconType: 'email' as const,
        },
        {
          text: '+1 416 694 8516',
          url: 'tel:+14166948516' as const,
          iconType: 'phone' as const,
        },
        {
          text: '411 Richmond St E Suite 103, Toronto, ON M5A 3S5',
          url: 'https://www.google.ca/maps/place/Envision/@43.6535858,-79.3668822,16z/data=!3m2!4b1!5s0x89d4cb3ecc719623:0xc79026c2ce9d7a6f!4m6!3m5!1s0x89d4cb3a2196599b:0x3f83ec84a88dc23e!8m2!3d43.6535858!4d-79.3668822!16s%2Fg%2F1tf7nk56?entry=ttu&g_ep=EgoyMDI0MTAxNi4wIKXMDSoASAFQAw%3D%3D' as const,
          iconType: 'location' as const,
        },
      ],
    },
  ];

  const sectionsToRender =
    sectionsData && sectionsData.length > 0 ? sectionsData : defaultSections;

  return (
    <div className='grid grid-cols-1 md:min-w-[412px] gap-6 tablet:grid-cols-2 desktop:grid-cols-4 desktop:col-span-2 tablet:mr-8 phone:mt-8 tablet:mt-8 desktop:mt-0'>
      {sectionsToRender.map((section, sectionIndex) => (
        <div key={sectionIndex} className='text-left phone:text-center'>
          <p className='font-medium text-white uppercase'>{section.title}</p>
          <ul
            className={`mt-8 phone:mx-auto phone:w-full ${
              section.sectionType === 'contact' ? 'space-y-2' : 'space-y-4'
            }`}
          >
            {section.links.map((link, linkIndex) => (
              <li key={linkIndex}>
                <Link
                  className={`text-gray-300 hover:text-white transition-colors duration-300 ${
                    section.sectionType === 'contact'
                      ? 'flex phone:justify-center tablet:justify-start gap-1.5 w-full'
                      : ''
                  } ${
                    link.iconType === 'location'
                      ? 'phone:items-center tablet:items-start'
                      : ''
                  }`}
                  href={link.url}
                  {...(link.url.startsWith('http') ||
                  link.url.startsWith('mailto:') ||
                  link.url.startsWith('tel:')
                    ? { target: '_blank' }
                    : {})}
                  {...(link.url.startsWith('mailto:')
                    ? { title: link.url.replace('mailto:', '') }
                    : {})}
                >
                  {section.sectionType === 'contact' &&
                    renderIcon(link.iconType)}
                  <span
                    className={`text-gray-300 ${
                      link.iconType === 'location'
                        ? 'not-italic phone:max-w-[220px] phone:text-center tablet:text-left tablet:max-w-[200px] phone:mt-0 tablet:-mt-0.5'
                        : ''
                    }`}
                  >
                    {link.text}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
